import { computed, type Ref } from 'vue'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { useHttp, useTranslate } from '@/shared/lib'
import { useNotification } from '@/shared/ui'
import {
  buildObjectPassportFieldsUrl,
  parseObjectPassportFieldsResponse,
} from './parseObjectPassportFieldsResponse'
import type { ObjectPassportFieldValueInput } from './types'
import type { ObjectPassportFieldsPayload } from './types'

type UseLoadObjectPassportFieldsOptions = {
  /** Дополнительный gate: запрос не уйдёт, пока false. */
  enabled?: Ref<boolean>
  /** Срез версии; пусто (или не передано) — текущие значения. */
  statusDate?: Ref<string | null>
}

/**
 * Загружает нормализованные поля и значения элемента:
 * GET /objects/:objectListId/passport?status_date= → payloads.fields + data (values).
 */
export const useLoadObjectPassportFields = (
  objectListId: Ref<number>,
  options: UseLoadObjectPassportFieldsOptions = {},
) => {
  const http = useHttp()
  const extraEnabled = options.enabled

  const url = computed(() => {
    const id = Number(objectListId.value)
    if (!Number.isFinite(id) || id <= 0) return ''
    return buildObjectPassportFieldsUrl(id, options.statusDate?.value)
  })

  const queryKey = computed(() => ['load_object_passport_fields', url.value])

  const enabled = computed(() => {
    if (!url.value) return false
    if (extraEnabled && !extraEnabled.value) return false
    return true
  })

  return useQuery({
    queryKey,
    enabled,
    retry: false,
    queryFn: async (): Promise<ObjectPassportFieldsPayload> => {
      const res = await http.get(url.value)
      // clearResponseData поднимает payloads → res.payload, data → res.data (values[]).
      return parseObjectPassportFieldsResponse({
        data: res.data,
        payloads: res.payload,
      })
    },
  })
}

type SaveObjectPassportFieldsInput = {
  objectListId: number
  values: ObjectPassportFieldValueInput[]
  /** Имя элемента; передаётся, когда изменилось (PUT /objects/:objectListId/passport). */
  name?: string | null
  /** Дата версии (локальное «настенное» время); пусто = серверный NOW(). */
  verDate?: string | null
}

/**
 * Сохраняет скалярные значения нормализованного паспорта:
 * PUT /objects/:objectListId/passport → { values, name? }.
 */
export const useSaveObjectPassportFields = () => {
  const http = useHttp()
  const { addError, addSuccess } = useNotification()
  const { trs } = useTranslate()

  const mutation = useMutation({
    mutationFn: ({ objectListId, values, name, verDate }: SaveObjectPassportFieldsInput) =>
      http.put(buildObjectPassportFieldsUrl(objectListId), {
        values,
        ...(name !== undefined ? { name } : {}),
        ...(verDate ? { ver_date: verDate } : {}),
      }),
  })

  const send = async (input: SaveObjectPassportFieldsInput) => {
    try {
      await mutation.mutateAsync(input)
      addSuccess(trs('itf.saved'))
      return true
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return false
    }
  }

  return {
    ...mutation,
    send,
  }
}

